import type { SupabaseClient } from '@supabase/supabase-js';
import { SERVER_URL, SUPABASE_ANON_KEY, SUPABASE_URL } from './config';
import { store } from './storage';

export interface Credentials {
  token: string;
  playerId: string;
  expiresAt: number;
}

/** A signed-in account's credential (optional; guests never have one). */
export interface AccountCredentials extends Credentials {
  accountId: string;
}

/**
 * Guest identity. With Supabase configured, players get an anonymous Supabase Auth session (stable user id,
 * refreshed automatically). Otherwise the game server issues a signed guest token. Either way the nickname
 * is just a display name; the token is what proves who you are on reconnect.
 */
let supabase: SupabaseClient | null = null;
/** The Supabase client is only downloaded when the app is configured to use it. */
async function sb() {
  if (!supabase && SUPABASE_URL && SUPABASE_ANON_KEY) {
    const { createClient } = await import('@supabase/supabase-js');
    supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, { auth: { persistSession: true, autoRefreshToken: true, storageKey: 'gmp.auth' } });
  }
  return supabase;
}

const GUEST_KEY = 'gmp.guest';
const ACCOUNT_KEY = 'gmp.account';
const DAY = 24 * 3600_000;

export function savedAccount(): AccountCredentials | null {
  const a = store.get<AccountCredentials>(ACCOUNT_KEY);
  return a && typeof a.token === 'string' && typeof a.accountId === 'string' && a.expiresAt > Date.now() ? a : null;
}

/** Signed in on this device: this account's credential is used instead of the guest one from now on. */
export function adoptAccount(c: AccountCredentials): void {
  store.set(ACCOUNT_KEY, { token: c.token, playerId: c.playerId, accountId: c.accountId, expiresAt: c.expiresAt });
}

/**
 * Signing out on this device. If this device's guest *is* the account's player (the account was made from
 * it), a fresh guest replaces it so the next person on this device isn't you; otherwise this device goes back
 * to its own earlier guest identity (and its parties).
 */
export async function forgetAccount(keepGuest = false): Promise<void> {
  const acc = store.get<AccountCredentials>(ACCOUNT_KEY);
  store.remove(ACCOUNT_KEY);
  if (!acc || keepGuest) return;
  const client = await sb();
  if (client) {
    const session = (await client.auth.getSession()).data.session;
    if (session?.user.id === acc.playerId) await client.auth.signOut().catch(() => undefined);
    return;
  }
  if (store.get<Credentials>(GUEST_KEY)?.playerId === acc.playerId) store.remove(GUEST_KEY);
}

/** Account credential, refreshed with the server when it is a week from expiring (or when asked). */
async function accountCredentials(forceRefresh: boolean): Promise<AccountCredentials | null> {
  const acc = savedAccount();
  if (!acc) return null;
  if (!forceRefresh && acc.expiresAt - Date.now() > 7 * DAY) return acc;
  try {
    const res = await fetch(`${SERVER_URL}/api/auth/refresh`, { method: 'POST', headers: { authorization: `Bearer ${acc.token}`, 'content-type': 'application/json' }, body: '{}' });
    if (res.ok) {
      const fresh = (await res.json()) as AccountCredentials;
      adoptAccount(fresh);
      return fresh;
    }
    if (res.status === 401 || res.status === 404) {
      // Signed out everywhere (or accounts switched off): back to being a guest on this device.
      await forgetAccount();
      return null;
    }
  } catch {
    /* offline: keep using the saved one while it lasts */
  }
  return acc.expiresAt - Date.now() > 60_000 ? acc : null;
}

export async function getCredentials(forceRefresh = false): Promise<Credentials> {
  const account = await accountCredentials(forceRefresh);
  if (account) return account;
  return guestCredentials(forceRefresh);
}

/** The device's guest identity (never the account's), e.g. to start a sign-in as a guest. */
async function guestCredentials(forceRefresh = false): Promise<Credentials> {
  const client = await sb();
  if (client) {
    let session = (await client.auth.getSession()).data.session;
    if (forceRefresh && session) session = (await client.auth.refreshSession()).data.session;
    if (!session) {
      const signed = await client.auth.signInAnonymously();
      if (signed.error || !signed.data.session) throw new Error(signed.error?.message ?? 'Could not start a guest session');
      session = signed.data.session;
    }
    return { token: session.access_token, playerId: session.user.id, expiresAt: (session.expires_at ?? 0) * 1000 };
  }
  const saved = store.get<Credentials>(GUEST_KEY);
  if (saved && !forceRefresh && saved.expiresAt - Date.now() > 24 * 3600_000) return saved;
  const res = await fetch(`${SERVER_URL}/api/guest`, { method: 'POST' });
  if (!res.ok) throw new Error(res.status === 429 ? 'Too many new sessions from this network. Wait a minute.' : 'Game server unavailable');
  const creds = (await res.json()) as Credentials;
  store.set(GUEST_KEY, creds);
  return creds;
}

/**
 * "Continue on another device": the guest credential as a link. Only for server-issued guest tokens (with
 * Supabase the other device signs in itself). Whoever holds the link can play as you until it expires,
 * which the screen says plainly.
 */
export function deviceLink(origin: string): string | null {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) return null;
  // Signed in: the other device signs in to the account instead (never hand an account token around).
  if (savedAccount()) return null;
  const saved = store.get<Credentials>(GUEST_KEY);
  if (!saved) return null;
  const packed = btoa(JSON.stringify(saved)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${origin}/device#g=${packed}`;
}

/** Reads a device link's credential from the URL hash, if it is well formed and not expired. */
export function credentialFromHash(hash: string): Credentials | null {
  const raw = new URLSearchParams(hash.replace(/^#/, '')).get('g');
  if (!raw) return null;
  try {
    const c = JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/'))) as Credentials;
    if (typeof c.token !== 'string' || typeof c.playerId !== 'string' || typeof c.expiresAt !== 'number' || c.token.length > 4096) return null;
    if (c.expiresAt < Date.now()) return null;
    return c;
  } catch {
    return null;
  }
}

/** Makes this device the player in the link (replacing this device's own guest identity). */
export function adoptCredential(c: Credentials): void {
  store.remove(ACCOUNT_KEY); // the link's player replaces any account sign-in on this device
  store.set(GUEST_KEY, c);
}
